import {normalizeSourceFileId} from './utils/sourceFileSelection';

export const SOURCES_ROOT_VIEW = Object.freeze({type: 'root'});

export const SOURCES_PRESET_FOLDERS = Object.freeze([
    {id: 'my', name: 'My files', fileCount: 0, isPreset: true},
    {id: 'shared', name: 'Shared with me', fileCount: 0, isPreset: true},
    {id: 'collected-materials', name: 'Collected Materials', fileCount: 0, isPreset: true},
]);

const USER_FOLDER_TOPIC_PREFIX = process.env.REACT_APP_SOURCES_USER_FOLDER_TOPIC_PREFIX || '/topic/data-items';

function getFirstDefined(...values) {
    return values.find((value) => value !== undefined && value !== null && value !== '');
}

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function toFiniteNumber(value, fallback = 0) {
    return Number.isFinite(value) ? value : fallback;
}

function getFileExtension(fileName) {
    if (typeof fileName !== 'string') {
        return null;
    }

    const normalizedName = fileName.trim().toLowerCase();
    const extension = normalizedName.includes('.') ? normalizedName.split('.').pop() : null;
    return extension || null;
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function looksLikeHtmlString(value) {
    return typeof value === 'string' && /<([a-z][^\s/>]*)(?:\s[^>]*)?>/i.test(value);
}

export function isProseMirrorDocJson(value) {
    return isPlainObject(value)
        && value.type === 'doc'
        && Array.isArray(value.content);
}

export function extractSourceRenderableContent(file = {}) {
    const prosemirrorCandidates = [
        file?.content,
        file?.contentJson,
        file?.pmJson,
        file?.prosemirror,
        file?.richContent,
        file?.raw?.content,
        file?.raw?.contentJson,
        file?.raw?.pmJson,
        file?.raw?.prosemirror,
        file?.raw?.richContent,
    ];

    const prosemirror = prosemirrorCandidates.find(isProseMirrorDocJson);
    if (prosemirror) {
        return {
            kind: 'prosemirror',
            value: prosemirror,
        };
    }

    const htmlCandidates = [
        file?.html,
        file?.rawHtml,
        file?.contentHtml,
        file?.raw?.html,
        file?.raw?.rawHtml,
        file?.raw?.contentHtml,
    ];

    const html = htmlCandidates.find(looksLikeHtmlString);
    if (html) {
        return {
            kind: 'html',
            value: html,
        };
    }

    const textCandidates = [
        file?.plainText,
        file?.text,
        file?.rawText,
        file?.description,
        file?.raw?.plainText,
        file?.raw?.text,
        file?.raw?.rawText,
        file?.raw?.description,
    ];

    const text = textCandidates.find((value) => typeof value === 'string' && value.trim());
    if (text) {
        return {
            kind: 'text',
            value: text,
        };
    }

    return {
        kind: 'none',
        value: null,
    };
}

export function inferSourceFileType(file = {}) {
    const explicitType = String(getFirstDefined(
        file?.fileType,
        file?.type,
        file?.itemType,
        file?.entityType,
        file?.mediaType,
        file?.kind,
        file?.format,
        '',
    )).toLowerCase();
    const mimeType = String(getFirstDefined(file?.mimeType, file?.contentType, file?.content_type, '')).toLowerCase();
    const extension = getFirstDefined(file?.extension, file?.ext, getFileExtension(file?.name), getFileExtension(file?.fileName));

    if (mimeType.startsWith('audio/') || mimeType === 'audio' || explicitType === 'audio'
            || ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) {
        return 'audio';
    }

    if (mimeType.startsWith('video/') || mimeType === 'video' || explicitType === 'video'
            || ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(extension)) {
        return 'video';
    }

    if (mimeType.startsWith('image/') || mimeType === 'image' || mimeType === 'product'
            || explicitType === 'image' || explicitType === 'product'
            || ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tiff'].includes(extension)) {
        return 'image';
    }

    if (mimeType === 'application/pdf' || mimeType === 'pdf' || extension === 'pdf' || explicitType === 'pdf') {
        return 'pdf';
    }

    if (mimeType.includes('html') || mimeType === 'html' || explicitType === 'html'
            || ['html', 'htm'].includes(extension)) {
        return 'html';
    }

    if (
        mimeType.startsWith('text/')
        || mimeType === 'text'
        || mimeType === 'txt'
        || ['txt', 'md', 'markdown', 'rtf', 'csv', 'json'].includes(extension)
        || explicitType === 'text'
        || explicitType === 'txt'
    ) {
        return 'text';
    }

    if (
        mimeType.includes('word')
        || mimeType.includes('officedocument')
        || mimeType === 'docx'
        || mimeType === 'document'
        || ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(extension)
        || ['document', 'doc', 'docx'].includes(explicitType)
    ) {
        return 'document';
    }

    return 'unknown';
}

function cloneFolder(folder, index) {
    const meta = isPlainObject(folder?.meta) ? folder.meta : {};
    const collectionTaskId = getFirstDefined(meta.collectionTaskId, folder?.collectionTaskId, null);
    const isVirtualCtFolder = Boolean(meta.virtualCtFolder || collectionTaskId);
    return {
        id: String(getFirstDefined(
            collectionTaskId,
            folder?.id,
            folder?.folderId,
            folder?.uuid,
            folder?.localId,
            `folder-${index}`,
        ) || `folder-${index}`),
        name: getFirstDefined(folder?.name, folder?.folderName, folder?.title, folder?.label, 'Untitled folder'),
        fileCount: toFiniteNumber(getFirstDefined(
            folder?.fileCount,
            meta.fileCount,
            folder?.itemCount,
            folder?.documentCount,
            folder?.documentsCount,
            folder?.count,
        )),
        topic: folder?.topic || null,
        branch: folder?.branch || null,
        isPreset: Boolean(folder?.isPreset),
        isSystem: Boolean(folder?.system || isVirtualCtFolder),
        collectionTaskId: collectionTaskId || null,
    };
}

function cloneFile(file, index) {
    const name = getFirstDefined(file?.name, file?.docTitle, file?.title, file?.fileName, file?.filename, file?.label, 'Untitled file');
    const mimeType = getFirstDefined(file?.mimeType, file?.contentType, file?.content_type, null);
    const extension = getFirstDefined(file?.extension, file?.ext, getFileExtension(name));

    return {
        id: normalizeSourceFileId(getFirstDefined(file?.id, file?.docId, file?.itemId, file?.uuid, file?.localId, `file-${index}`)),
        referencedDataItemId: file?.referencedDataItemId || null,
        ownerId: getFirstDefined(file?.ownerId, file?.owner, file?.userId, null),
        sharedByUserId: file?.sharedByUserId || null,
        sharedBy: file?.sharedBy || null,
        name,
        status: getFirstDefined(file?.status, file?.raw?.status, file?.meta?.processing?.status) || null,
        processingStage: getFirstDefined(
            file?.stage,
            file?.meta?.processing?.stage,
            file?.raw?.meta?.processing?.stage,
        ) || null,
        processingError: file?.meta?.processing?.errorMessage || file?.raw?.meta?.processing?.errorMessage || null,
        source: getFirstDefined(file?.source, file?.docSource, file?.docSourceType, file?.classificationLevel, null),
        fileType: inferSourceFileType({...file, name, mimeType, extension}),
        mimeType,
        extension,
        url: getFirstDefined(file?.url, file?.downloadUrl, file?.signedUrl, file?.fileUrl, null),
        cmId: getFirstDefined(file?.meta?.cmId, file?.cmId, null),
        cmStatus: getFirstDefined(file?.meta?.cmStatus, file?.cmStatus, null),
        cmSubmittedAt: getFirstDefined(file?.meta?.cmSubmittedAt, file?.cmSubmittedAt, null),
        cmNumber: getFirstDefined(file?.meta?.cmNumber, file?.cmNumber, null),
        cmCreatedBy: getFirstDefined(file?.meta?.cmCreatedBy, file?.cmCreatedBy, null),
        cmCreatorName: getFirstDefined(file?.meta?.cmCreatorName, file?.cmCreatorName, null),
        raw: file,
    };
}

function isFolderLike(item) {
    return Boolean(
        item?.isPreset
        || item?.systemFolderKind
        || item?.folderId
        || item?.folderName
        || item?.type === 'folder'
        || item?.type === 'FOLDER'
        || item?.entityType === 'folder'
        || item?.entityType === 'FOLDER'
    );
}

function extractFolders(payload) {
    const candidateCollections = [
        payload?.folders,
        payload?.data?.folders,
        payload?.result?.folders,
        payload?.items,
        payload?.dataItems,
        Array.isArray(payload) ? payload : null,
    ];

    const folders = candidateCollections.flatMap((collection) => toArray(collection).filter(isFolderLike));
    return folders.map(cloneFolder);
}

function extractFiles(payload) {
    const candidateCollections = [
        payload?.files,
        payload?.documents,
        payload?.items,
        payload?.dataItems,
        payload?.recentFiles,
        payload?.data?.files,
        payload?.data?.documents,
        payload?.result?.files,
        payload?.result?.documents,
        Array.isArray(payload) ? payload : null,
    ];

    const files = candidateCollections.flatMap((collection) => toArray(collection).filter((item) => !isFolderLike(item)));
    return files.map(cloneFile);
}

function dedupeById(items) {
    const uniqueItems = new Map();

    items.forEach((item, index) => {
        const id = item?.id || `item-${index}`;
        if (!uniqueItems.has(id)) {
            uniqueItems.set(id, item);
        }
    });

    return Array.from(uniqueItems.values());
}

export function mapRealtimePayloadToViewModel(view, payload) {
    const normalizedFolders = dedupeById(extractFolders(payload));
    const normalizedFiles = dedupeById(extractFiles(payload));

    if (!view || view.type === 'root') {
        return {
            folders: SOURCES_PRESET_FOLDERS.map(cloneFolder),
            files: normalizedFiles,
        };
    }

    return {
        folders: normalizedFolders,
        files: normalizedFiles,
    };
}

export function buildSourcesTopic(view) {
    const encodedUserId = view?.userId ? encodeURIComponent(view.userId) : null;

    if (!view || view.type === 'root') {
        return encodedUserId ? `/topic/data-items/root/${encodedUserId}` : '/topic/data-items/root';
    }

    if (view.type === 'preset') {
        if (view.id === 'all') {
            return '/topic/data-items/all';
        }

        if (encodedUserId && (view.id === 'my' || view.id === 'shared')) {
            return `/topic/data-items/${view.id}/${encodedUserId}`;
        }

        if (view.id === 'collected-materials' || view.id === 'collected') {
            return encodedUserId
                ? `/topic/data-items/${encodedUserId}/collected-materials`
                : '/topic/data-items/collected-materials';
        }

        return `/topic/data-items/${view.id}`;
    }

    if (view.type === 'folder' && view.id) {
        if (encodedUserId) {
            return `${USER_FOLDER_TOPIC_PREFIX}/${encodedUserId}/folder/${view.id}`;
        }

        return `/topic/data-items/folder/${view.id}`;
    }

    return encodedUserId ? `/topic/data-items/root/${encodedUserId}` : '/topic/data-items/root';
}

export function normalizeRealtimePayload(messageBody) {
    if (typeof messageBody !== 'string') {
        return {raw: messageBody, payload: messageBody, parseStatus: 'non-string'};
    }

    try {
        return {
            raw: messageBody,
            payload: JSON.parse(messageBody),
            parseStatus: 'parsed',
        };
    } catch (error) {
        return {
            raw: messageBody,
            payload: null,
            parseStatus: 'raw',
            parseError: error?.message || 'Failed to parse JSON payload',
        };
    }
}

export function filterSourcesByQuery(viewModel, query) {
    const normalizedQuery = (query || '').trim().toLowerCase();
    if (!normalizedQuery) return viewModel;

    return {
        folders: viewModel.folders.filter(folder => folder.name.toLowerCase().includes(normalizedQuery)),
        files: viewModel.files.filter(file => file.name.toLowerCase().includes(normalizedQuery)),
    };
}


